import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Lightbulb, AlertTriangle, XCircle, CheckCircle, MapPin, TrendingUp } from 'lucide-react';
import { usePostesIluminacao } from '../context/PostesIluminacaoProvider';
import { STATUS_COLORS } from '../utils/constants';

export default function DashboardEstatisticas() {
  const { obterEstatisticas, localidades, postes, lastUpdate } = usePostesIluminacao();
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    funcionando: 0,
    alerta: 0,
    defeito: 0,
    porcentagemFuncionando: 0,
    porTipoLampada: {},
    porLocalidade: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarEstatisticas();
  }, []);

  // Recarregar estatísticas quando houver atualizações
  useEffect(() => {
    if (postes.length > 0) {
      carregarEstatisticas();
    }
  }, [lastUpdate]);

  const carregarEstatisticas = async () => {
    setLoading(true);
    try {
      const dados = await obterEstatisticas();
      setEstatisticas(dados);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Preparar dados para gráficos
  const dadosStatus = [
    { name: 'Funcionando', value: estatisticas.funcionando, color: STATUS_COLORS.Funcionando },
    { name: 'Alerta', value: estatisticas.alerta, color: STATUS_COLORS.Alerta },
    { name: 'Defeito', value: estatisticas.defeito, color: STATUS_COLORS.Defeito }
  ];

  const dadosTipoLampada = Object.entries(estatisticas.porTipoLampada).map(([tipo, quantidade]) => ({
    name: tipo,
    value: quantidade
  }));

  const dadosLocalidade = Object.entries(estatisticas.porLocalidade).map(([localidadeId, quantidade]) => {
    const localidade = localidades.find(l => l.id.toString() === localidadeId.toString());
    return {
      name: localidade ? localidade.nome : `Bairro ${localidadeId}`,
      value: quantidade
    };
  });

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Carregando estatísticas...</p>
      </div>
    );
  }

  return (
    <div className="postes-iluminacao">
      <div className="dashboard-full-container">
        <div className="dashboard-header">
          <h1>
            <Lightbulb size={24} color="#ff6f00" />
            Dashboard - Postes de Iluminação Pública
          </h1>
          <p>Visão geral do sistema de iluminação pública municipal</p>
        </div>

        <div className="dashboard-content">

      {/* Cards de Estatísticas Principais */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <Lightbulb size={24} />
          </div>
          <div className="stat-content">
            <h3>Total de Postes</h3>
            <p className="stat-number">{estatisticas.total}</p>
            <span className="stat-label">Cadastrados no sistema</span>
          </div>
        </div>

        <div className="stat-card funcionando">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>Funcionando</h3>
            <p className="stat-number">{estatisticas.funcionando}</p>
            <span className="stat-label">{estatisticas.porcentagemFuncionando}% do total</span>
          </div>
        </div>

        <div className="stat-card alerta">
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <h3>Em Alerta</h3>
            <p className="stat-number">{estatisticas.alerta}</p>
            <span className="stat-label">Necessitam atenção</span>
          </div>
        </div>

        <div className="stat-card defeito">
          <div className="stat-icon">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>Com Defeito</h3>
            <p className="stat-number">{estatisticas.defeito}</p>
            <span className="stat-label">Necessitam reparo</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="charts-grid">
        {/* Gráfico de Status */}
        <div className="chart-card">
          <h3>
            <TrendingUp size={20} />
            Distribuição por Status
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dadosStatus}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {dadosStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Tipos de Lâmpada */}
        {dadosTipoLampada.length > 0 && (
          <div className="chart-card">
            <h3>
              <Lightbulb size={20} />
              Postes por Tipo de Lâmpada
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosTipoLampada}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#667eea" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Gráfico de Localidades */}
        {dadosLocalidade.length > 0 && (
          <div className="chart-card">
            <h3>
              <MapPin size={20} />
              Postes por Localidade
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosLocalidade}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Resumo de Manutenção */}
      <div className="maintenance-summary">
        <h3>Resumo de Manutenção</h3>
        <div className="maintenance-cards">
          <div className="maintenance-card priority-high">
            <h4>Alta Prioridade</h4>
            <p className="maintenance-number">{estatisticas.defeito}</p>
            <span>Postes com defeito</span>
          </div>
          <div className="maintenance-card priority-medium">
            <h4>Média Prioridade</h4>
            <p className="maintenance-number">{estatisticas.alerta}</p>
            <span>Postes em alerta</span>
          </div>
          <div className="maintenance-card priority-low">
            <h4>Funcionando</h4>
            <p className="maintenance-number">{estatisticas.funcionando}</p>
            <span>Postes operacionais</span>
          </div>
        </div>
        </div>
      </div>
    </div>


    </div>
  );
}